import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScopeDriftPill } from "../components/quotes/ScopeDriftBanner";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { WhatsAppSendButton } from "../components/WhatsAppSendButton";
import {
  ArrowLeft, CurrencyGbp, MapPin, User, Phone, EnvelopeSimple, Clock,
  Plus, Trash, Star, PencilSimple, CheckCircle, ArrowRight, Download,
  Paperclip, File as FileIcon, X, Warning, ArrowsClockwise, Note,
  CopySimple, PaperPlaneTilt, LinkSimple, Briefcase, CalendarPlus,
  CaretDown, House, Truck, Receipt, Crown, Check, TreeStructure
} from "@phosphor-icons/react";
import SplitIssuesEditor, { issuesFromText, selectedIssues } from "../components/jobs/SplitIssuesEditor";
import ScopeOfWorksCard from "../components/jobs/ScopeOfWorksCard";
import ScopeOfWorksField, { CaveatsField } from "../components/jobs/ScopeOfWorksField";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import What3WordsLink from "../components/What3WordsLink";
import { FileDropZone } from "../components/FileDropZone";
import { NoticeBox } from "../components/NoticeBox";
import { NoticeEditor, EMPTY_NOTICE } from "../components/NoticeEditor";
import PrivateNotes from "../components/PrivateNotes";
import NotesThread from "../components/notes/NotesThread";
import { ClientWaitingPill } from "../components/notes/ClientWaiting";
import { useClientWaiting } from "../hooks/useClientWaiting";
import EntityFiles from "../components/EntityFiles";
import SupplierPicker from "../components/SupplierPicker";
import QuoteComparisonDialog from "../components/QuoteComparisonDialog";
import { useAuth } from "../contexts/AuthContext";
import { ImageLightbox } from "../components/ImageLightbox";
import AuthImage from "../components/AuthImage";
import { openAuthFile } from "../lib/fileOpen";
import ReverseCalcDialog from "../components/ReverseCalcDialog";
import NearbySuppliers from "../components/NearbySuppliers";
import AreaMatchSuppliers from "../components/AreaMatchSuppliers";
import SLAActionPanel from "../components/SLAActionPanel";
import AckEmailStatusBadge from "../components/AckEmailStatusBadge";
import { PortalPushButton, PortalLiveBadge, ClientDecisionBanner } from "../components/quotes/PortalQuoteActions";
import QuoteQuickActions from "../components/quotes/QuoteQuickActions";
import PropertyAccessCard from "../components/PropertyAccessCard";
import AccessNotesField from "../components/AccessNotesField";
import { ClientUpdatesList } from "../components/portal/ClientUpdates";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
import MarkupFields from "../components/quotes/MarkupFields";
import ResidentReportCard from "../components/resident/ResidentReportCard";
import CreateRamsButton from "../components/hs/CreateRamsButton";
import RamsFlag from "../components/hs/RamsFlag";
import QuickIssueRams from "../components/hs/QuickIssueRams";
import RamsAfterConvertDialog from "../components/hs/RamsAfterConvertDialog";
const STATUS_MAP = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  awaiting_quotes: { label: "Awaiting Quotes", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
  quotes_received: { label: "Quotes Received", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400" },
  submitted_to_client: { label: "Quote with Client", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400" },
  client_approved: { label: "Client Approved", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" },
  converting: { label: "Converting", color: "bg-cyan-100 text-cyan-700" },
  converted: { label: "Converted", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  declined: { label: "Declined", color: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
  on_hold: { label: "On Hold", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400" },
};

const NEXT_STATUS = {
  draft: ["awaiting_quotes", "on_hold", "declined"],
  awaiting_quotes: ["quotes_received", "on_hold", "declined"],
  quotes_received: ["on_hold", "declined"],
  submitted_to_client: ["client_approved", "declined", "on_hold"],
  client_approved: ["converting"],
  on_hold: ["draft", "awaiting_quotes", "quotes_received", "declined"],
};

export default function JobRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { byId: waitingById } = useClientWaiting();
  const { user } = useAuth();
  const [jr, setJr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(searchParams.get("tab") === "notes" ? "notes" : "quotes");

  // Quote form
  const [showAddQuote, setShowAddQuote] = useState(false);
  // Phase 292 — side-by-side quote comparison dialog
  const [showCompare, setShowCompare] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ vendor_id: "", vendor_name: "", vendor_email: "", vendor_phone: "", vendor_company: "", amount: "", description: "", validity_date: "", attachments: [], markup_a_pct: 0, markup_b_pct: 0, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up" });
  const [addingQuote, setAddingQuote] = useState(false);
  const [markupTemplates, setMarkupTemplates] = useState([]);
  // Cached list of onboarded suppliers for the picker in the Add Quote dialog
  const [onboardedSuppliers, setOnboardedSuppliers] = useState([]);
  const [reverseCalcQuote, setReverseCalcQuote] = useState(null);
  const [showNearbySuppliers, setShowNearbySuppliers] = useState(false);
  const [counteringQuote, setCounteringQuote] = useState(null);
  const [counterForm, setCounterForm] = useState({ counter_start_date: "", note: "" });

  // Dialogs
  const [showConvert, setShowConvert] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [serviceOptions, setServiceOptions] = useState([]);
  useEffect(() => {
    API.get("/suppliers/service-options").then(({ data }) => setServiceOptions(data.services || [])).catch(() => {});
  }, []);

  // Edit quote
  const [editingQuote, setEditingQuote] = useState(null);
  const [editQuoteForm, setEditQuoteForm] = useState({});

  // Request supplier quotes
  const [showRequestQuotes, setShowRequestQuotes] = useState(false);
  const [vendorEmailsInput, setVendorEmailsInput] = useState("");
  const [sendingQuoteRequests, setSendingQuoteRequests] = useState(false);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  // Client approval link
  const [showApprovalLink, setShowApprovalLink] = useState(false);
  const [approvalLink, setApprovalLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  // Convert to Job
  const [showConvertJob, setShowConvertJob] = useState(false);
  const [convertingJob, setConvertingJob] = useState(false);
  const [ramsAfter, setRamsAfter] = useState({ open: false, jobId: "", jobRef: "" });
  const [convertCreateWO, setConvertCreateWO] = useState(true);
  const [notifyLosingSuppliers, setNotifyLosingSuppliers] = useState(true);
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [splitRows, setSplitRows] = useState([]);
  const openConvertJob = () => {
    setSplitEnabled(false);
    setSplitRows(issuesFromText(jr?.request_details));
    setShowConvertJob(true);
  };

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [builderQuotes, setBuilderQuotes] = useState({});

  const fetchJR = useCallback(async () => {
    try {
      const { data } = await API.get(`/job-requests/${id}`);
      setJr(data);
      API.get(`/quotes?job_request_id=${id}&limit=50`).then(({ data: b }) => setBuilderQuotes(Object.fromEntries((b.quotes || []).map(q => [q.id, q])))).catch(() => {});
    } catch { toast.error("Failed to load job request"); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchJR(); }, [fetchJR]);
  useEffect(() => { API.get("/markup-templates").then(({ data }) => setMarkupTemplates(data.templates || [])).catch(() => {}); }, []);
  // Onboarded suppliers — admin can pick from this list when adding a
  // supplier quote so vendor_name / email / phone / company auto-fill and
  // the resulting quote is tied back to the supplier record via vendor_id.
  // Falls back to manual entry when the supplier isn't in the system.
  useEffect(() => {
    API.get("/suppliers?limit=500").then(({ data }) => {
      setOnboardedSuppliers(Array.isArray(data) ? data : (data.vendors || data.suppliers || []));
    }).catch(() => {});
  }, []);

  const handleStatusChange = async (status) => {
    try {
      await API.put(`/job-requests/${id}/status`, { status });
      toast.success(`Status updated to ${STATUS_MAP[status]?.label}`);
      fetchJR();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const handleAddQuote = async (e) => {
    e.preventDefault();
    if (!quoteForm.vendor_name.trim() || !quoteForm.amount) { toast.error("Supplier name and amount are required"); return; }
    setAddingQuote(true);
    try {
      await API.post(`/job-requests/${id}/quotes`, { ...quoteForm, amount: parseFloat(quoteForm.amount) || 0, markup_a_pct: parseFloat(quoteForm.markup_a_pct) || 0, markup_b_pct: parseFloat(quoteForm.markup_b_pct) || 0 });
      toast.success("Quote added");
      setShowAddQuote(false);
      setQuoteForm({ vendor_id: "", vendor_name: "", vendor_email: "", vendor_phone: "", vendor_company: "", amount: "", description: "", validity_date: "", attachments: [], markup_a_pct: 0, markup_b_pct: 0, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up" });
      fetchJR();
    } catch { toast.error("Failed to add quote"); }
    finally { setAddingQuote(false); }
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      await API.delete(`/job-requests/${id}/quotes/${quoteId}`);
      toast.success("Quote removed");
      fetchJR();
    } catch { toast.error("Failed to delete quote"); }
  };

  const handleSelectQuote = async (quoteId) => {
    try {
      await API.put(`/job-requests/${id}/select-quote/${quoteId}`);
      toast.success("Quote selected");
      fetchJR();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to select quote");
    }
  };

  const handleCounterSubmit = async () => {
    if (!counteringQuote) return;
    if (!counterForm.counter_start_date) { toast.error("Please pick a counter start date"); return; }
    try {
      await API.post(`/job-requests/${id}/counter-start-date/${counteringQuote.id}`, {
        counter_start_date: counterForm.counter_start_date,
        note: counterForm.note.trim(),
      });
      toast.success("Counter proposal sent to supplier");
      setCounteringQuote(null);
      setCounterForm({ counter_start_date: "", note: "" });
      fetchJR();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send counter");
    }
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      const { data } = await API.post(`/job-requests/${id}/convert-to-project`);
      toast.success(`Converted to project ${data.project_ref}`);
      navigate(`/projects/${data.project_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to convert"); }
    finally { setConverting(false); }
  };

  const handleConvertToJob = async () => {
    const issues = splitEnabled ? selectedIssues(splitRows) : [];
    if (splitEnabled && issues.length < 1) { toast.error("Tick at least one issue to split into sub-jobs"); return; }
    setConvertingJob(true);
    try {
      const { data } = await API.post(`/job-requests/${id}/convert-to-job`, {
        create_works_order: splitEnabled ? false : convertCreateWO,
        notify_losing_suppliers: notifyLosingSuppliers,
        ...(issues.length ? { sub_jobs: issues } : {}),
      });
      toast.success(data.message || `Converted to job ${data.job_ref}`);
      if (data.works_order_id) {
        // Linger the toast so the admin sees the WO number, then deep-link to the WO
        toast.success(`Draft Works Order ${data.wo_number} created`);
      }
      if (data.losing_suppliers_notified) {
        toast.success(`${data.losing_suppliers_notified} losing supplier(s) notified`);
      }
      setShowConvertJob(false);
      if (data.rams) {
        toast.success(`RAMS ${data.rams.reference} carried over to ${data.job_ref} and refreshed`);
        navigate(`/jobs/${data.job_id}`);
      } else {
        setRamsAfter({ open: true, jobId: data.job_id, jobRef: data.job_ref, autoPending: !!data.rams_auto_pending });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to convert to job"); }
    finally { setConvertingJob(false); }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/job-requests/${id}`);
      toast.success("Job request deleted");
      navigate("/job-requests");
    } catch { toast.error("Failed to delete"); }
  };

  const handleSaveDetails = async () => {
    try {
      await API.put(`/job-requests/${id}`, editForm);
      toast.success("Details updated");
      setShowEditDetails(false);
      fetchJR();
    } catch { toast.error("Failed to update"); }
  };

  const handleAttachmentsChange = async (newFiles) => {
    try {
      await API.put(`/job-requests/${id}`, { attachments: newFiles });
      toast.success("Attachments updated");
      fetchJR();
    } catch { toast.error("Failed to update attachments"); }
  };

  const handleExportPDF = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/job-requests/${id}/export-pdf`, "_blank");
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await API.post(`/job-requests/${id}/duplicate`);
      toast.success(`Duplicated as ${data.reference}`);
      navigate(`/job-requests/${data.id}`);
    } catch { toast.error("Failed to duplicate"); }
  };

  const handleEditQuote = async (e) => {
    e.preventDefault();
    if (!editingQuote) return;
    try {
      await API.put(`/job-requests/${id}/quotes/${editingQuote}`, { ...editQuoteForm, amount: parseFloat(editQuoteForm.amount) || 0, markup_a_pct: parseFloat(editQuoteForm.markup_a_pct) || 0, markup_b_pct: parseFloat(editQuoteForm.markup_b_pct) || 0 });
      toast.success("Quote updated");
      setEditingQuote(null);
      fetchJR();
    } catch { toast.error("Failed to update quote"); }
  };

  const handleRequestVendorQuotes = async () => {
    const manualEmails = vendorEmailsInput.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
    const supplierEmails = selectedSuppliers.map(s => s.email).filter(Boolean);
    const emails = [...new Set([...supplierEmails, ...manualEmails])];
    if (!emails.length) { toast.error("Select at least one supplier or enter an email"); return; }
    setSendingQuoteRequests(true);
    try {
      const { data } = await API.post(`/job-requests/${id}/request-vendor-quotes`, { vendor_emails: emails });
      toast.success(`Quote requests sent to ${data.sent} supplier(s)`);
      setShowRequestQuotes(false);
      setVendorEmailsInput("");
      setSelectedSuppliers([]);
      fetchJR();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setSendingQuoteRequests(false); }
  };

  const openInviteDialog = async () => {
    setShowRequestQuotes(true);
    try {
      const { data } = await API.get("/suppliers");
      const list = Array.isArray(data) ? data : data.vendors || [];
      setAvailableSuppliers(list.filter(v => v.email));
    } catch {}
  };

  const toggleSupplier = (supplier) => {
    setSelectedSuppliers(prev =>
      prev.find(s => s.id === supplier.id)
        ? prev.filter(s => s.id !== supplier.id)
        : [...prev, supplier]
    );
  };

  const filteredSuppliers = [...availableSuppliers]
    .sort((a, b) => (a.company_name || a.contact_name || "").localeCompare(b.company_name || b.contact_name || "", undefined, { sensitivity: "base" }))
    .filter(s => {
      const q = supplierSearch.trim().toLowerCase();
      if (!q) return true;
      const hay = [
        s.company_name, s.contact_name, s.name, s.email, s.phone, s.trade,
        s.postcode, s.town_city, s.county, s.coverage_area,
        Array.isArray(s.sectors) ? s.sectors.join(" ") : s.sectors,
        Array.isArray(s.areas_covered) ? s.areas_covered.join(" ") : s.areas_covered,
      ].filter(Boolean).join(" ").toLowerCase();
      return q.split(/\s+/).filter(Boolean).every(w => hay.includes(w));
    });

  const handleGenerateApprovalLink = async (sendEmail = false) => {
    setGeneratingLink(true);
    try {
      const { data } = await API.post(`/job-requests/${id}/client-approval-link`, { send_email: sendEmail });
      setApprovalLink(data.link);
      setShowApprovalLink(true);
      if (sendEmail) toast.success("Approval link emailed to client");
      fetchJR();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate link");
    } finally { setGeneratingLink(false); }
  };

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const allImages = (jr?.attachments || []).filter(a => a.type?.startsWith("image/"));

  if (loading || !jr) {
    return <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  const st = jr.converted_job_id
    ? STATUS_MAP.converted
    : (STATUS_MAP[jr.status] || STATUS_MAP.draft);
  const selectedQuote = jr.quotes?.find(q => q.id === jr.selected_quote_id);
  const nextStatuses = NEXT_STATUS[jr.status] || [];
  const markUpdatesSeen = async () => {
    try { await API.post(`/job-requests/${id}/client-updates/seen`); fetchJR(); } catch { toast.error("Could not update"); }
  };
  const selectedQuoteTotal = selectedQuote ? (selectedQuote.amount || 0) * (1 + (selectedQuote.markup_a_pct || 0) / 100) * (1 + (selectedQuote.markup_b_pct || 0) / 100) : null;
  const canConvert = !jr.converted_project_id && !jr.converted_job_id && !["cancelled", "declined"].includes(jr.status);
  const canConvertToJob = !jr.converted_job_id && jr.status !== "cancelled";

  return (
    <div data-testid="job-request-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <Link to="/job-requests"><Button variant="ghost" size="sm"><ArrowLeft size={16} /></Button></Link>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-muted-foreground">{jr.reference}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.color}`} data-testid="jr-status-badge">{st.label}</span>
              {jr.source === "client_portal" && <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" data-testid="jr-source-portal">Client Portal</span>}
              {jr.source === "email" && <Link to="/inbox" className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 hover:underline" title={`From ${jr.source_email_from || "email"}${jr.source_email_subject ? ` — ${jr.source_email_subject}` : ""}`} data-testid="jr-source-email">From email{jr.source_email_from ? ` · ${jr.source_email_from}` : ""}</Link>}
              {jr.request_type === "project" && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-violet-600 text-white" title={jr.desired_start_date ? `Desired start ${jr.desired_start_date}` : ""} data-testid="jr-project-badge">Project Request{jr.desired_start_date ? ` · start ${new Date(jr.desired_start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}{jr.estimated_budget > 0 ? ` · ~£${Number(jr.estimated_budget).toLocaleString()}` : ""}</span>}
              {jr.price_mandate && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" title={jr.mandate_note || ""} data-testid="jr-mandate-badge">Price Mandate £{Number(jr.mandate_limit || 0).toLocaleString()} — proceed without quotes</span>}
              <RamsFlag status={jr.rams_status} ramsId={jr.rams_id} testId="jr-rams-flag" />
              <QuickIssueRams status={jr.rams_status} ramsId={jr.rams_id} onIssued={fetchJR} className="h-5 px-2 text-[10px] rounded-full" testId="jr-quick-issue-rams" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight" data-testid="jr-title">{jr.title}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Created {new Date(jr.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} by {jr.created_by_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="jr-export-pdf"><Download size={14} className="mr-1" /> PDF</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/job-requests/${id}/export-brief-pdf`, "_blank")} data-testid="jr-export-brief-pdf"><Download size={14} className="mr-1" /> Supplier Brief</Button>
          <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="jr-duplicate-btn"><CopySimple size={14} className="mr-1" /> Duplicate</Button>
          <Button variant="outline" size="sm" onClick={() => setShowNearbySuppliers(s => !s)} data-testid="jr-nearby-btn"><MapPin size={14} className="mr-1" /> {showNearbySuppliers ? "Hide" : "Find"} Nearby Suppliers</Button>
          <CreateRamsButton entityType="job_request" entityId={id} testId="jr-create-rams-btn" />
          <Button variant="outline" size="sm" onClick={() => { setEditForm({ title: jr.title, client_name: jr.client_name, client_email: jr.client_email, client_phone: jr.client_phone, client_company: jr.client_company, property_address: jr.property_address, what3words: jr.what3words || "", access_notes: jr.access_notes || "", request_details: jr.request_details, scope_of_works: jr.scope_of_works || "", caveats: jr.caveats || "", category: jr.category, service_required: jr.service_required || "", priority: jr.priority, requested_completion_date: jr.requested_completion_date, estimated_budget: jr.estimated_budget, notice: { ...EMPTY_NOTICE, ...(jr.notice || {}) } }); setShowEditDetails(true); }} data-testid="jr-edit-btn"><PencilSimple size={14} className="mr-1" /> Edit</Button>
          {canConvert && <Button onClick={() => setShowConvert(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="jr-convert-btn"><ArrowRight size={14} className="mr-1" /> Convert to Project</Button>}
          {canConvertToJob && <Button onClick={openConvertJob} className="bg-blue-600 hover:bg-blue-700" data-testid="jr-convert-job-btn"><Briefcase size={14} className="mr-1" /> Convert to Job</Button>}
          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setShowDelete(true)} data-testid="jr-delete-btn"><Trash size={14} /></Button>
        </div>
      </div>

      <NoticeBox notice={jr.notice} className="mb-4" testid="jr-notice" />

      {/* Status actions */}
      {nextStatuses.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 p-3 border border-border rounded-md bg-muted/30" data-testid="jr-status-actions">
          <span className="text-xs text-muted-foreground self-center mr-1">Move to:</span>
          {nextStatuses.map(s => (
            <Button key={s} variant="outline" size="sm" className="text-xs h-7" onClick={() => handleStatusChange(s)} data-testid={`jr-status-${s}`}>
              {STATUS_MAP[s]?.label || s}
            </Button>
          ))}
          <div className="flex-1" />
          {["draft", "awaiting_quotes", "quotes_received"].includes(jr.status) && (jr.quotes || []).length > 0 && (
            <PortalPushButton url={`/job-requests/${id}/send-to-portal`} amount={selectedQuoteTotal} onDone={fetchJR} testId="jr-send-to-portal-btn" size="sm" className="text-xs h-7"
              disabledReason={!jr.selected_quote_id ? "Select the quote the client should see first (Quotes tab)" : !jr.client_id ? "Link this request to a client with portal access" : ""} />
          )}
          {jr.status === "submitted_to_client" && (
            <PortalLiveBadge pushedAt={jr.portal_quote?.pushed_at || jr.updated_at} notified={jr.portal_quote?.notified} withdrawUrl={`/job-requests/${id}/withdraw-from-portal`} onDone={fetchJR} testId="jr-portal-live" />
          )}
          {["draft", "awaiting_quotes", "quotes_received"].includes(jr.status) && (
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={openInviteDialog} data-testid="jr-request-quotes-btn">
              <PaperPlaneTilt size={12} className="mr-1" /> Invite Supplier
            </Button>
          )}
          {jr.status === "quotes_received" && jr.selected_quote_id && (
            <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleGenerateApprovalLink(false)} disabled={generatingLink} data-testid="jr-approval-link-btn">
              <LinkSimple size={12} className="mr-1" /> {generatingLink ? "Generating..." : "Client Approval Link"}
            </Button>
          )}
        </div>
      )}

      {/* Stale indicator */}
      {(() => {
        const daysSinceUpdate = Math.floor((Date.now() - new Date(jr.updated_at).getTime()) / (1000 * 60 * 60 * 24));
        if (daysSinceUpdate >= 7 && !["converted", "declined"].includes(jr.status)) {
          return (
            <div className="mb-4 p-2.5 border border-amber-200 dark:border-amber-800 rounded-md bg-amber-50 dark:bg-amber-900/20 flex items-center gap-2" data-testid="jr-stale-indicator">
              <Warning size={16} className="text-amber-600 shrink-0" />
              <span className="text-xs text-amber-700 dark:text-amber-400">This request hasn't been updated in {daysSinceUpdate} days. Consider following up or updating the status.</span>
            </div>
          );
        }
        return null;
      })()}

      {jr.client_response?.via === "client_portal" && ["client_approved", "declined", "converting", "converted"].includes(jr.status) && (
        <div className="mb-4">
          <ClientDecisionBanner response={jr.client_response} amount={selectedQuoteTotal} testId="jr-client-decision"
            convertLabel={jr.request_type === "project" ? "Convert to project" : "Convert to job"}
            onConvert={jr.status === "client_approved" ? () => (jr.request_type === "project" ? setShowConvert(true) : setShowConvertJob(true)) : null} />
        </div>
      )}

      {/* Phase 253 — Acknowledge → Action SLA */}
      <SLAActionPanel entityType="job-requests" entity={jr} onChange={fetchJR} />

      {/* Phase 259 — Ack email status */}
      {jr.has_unseen_client_update && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30 px-3 py-2 text-xs" data-testid="jr-client-updates-chip">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="font-semibold text-emerald-800 dark:text-emerald-200">Client added details</span>
          <span className="text-muted-foreground">— {(jr.client_updates || []).filter(u => !u.seen_at).length} new update(s) under Request Details</span>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] ml-auto text-emerald-800 dark:text-emerald-200" onClick={markUpdatesSeen} data-testid="jr-client-updates-chip-seen">Mark as seen</Button>
        </div>
      )}
      <AckEmailStatusBadge entityType="job_request" entity={jr} onChange={fetchJR} />

      {/* Nearby Suppliers (Phase 231j) */}
      {showNearbySuppliers && (jr.latitude || jr.property_address || jr.street_address || jr.postcode) && (
        <div className="mb-4" data-testid="jr-nearby-suppliers-panel">
          <NearbySuppliers
            jrId={id}
            radiusMiles={50}
            limit={8}
            title="Suppliers closest to this property"
            showTradeInput
            showAutoPick
            includeCompliance
            data-testid="jr-nearby-suppliers"
          />
        </div>
      )}

      {/* Converted project link */}
      {jr.converted_project_id && (
        <div className="mb-4 p-3 border border-emerald-200 dark:border-emerald-800 rounded-md bg-emerald-50 dark:bg-emerald-900/20" data-testid="jr-converted-link">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-600" weight="fill" />
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Converted to Project</span>
            <Link to={`/projects/${jr.converted_project_id}`} className="text-sm text-primary underline ml-1">View Project</Link>
          </div>
        </div>
      )}

      {/* Converted job link */}
      {jr.converted_job_id && (
        <div className="mb-4 p-3 border border-blue-200 dark:border-blue-800 rounded-md bg-blue-50 dark:bg-blue-900/20" data-testid="jr-converted-job-link">
          <div className="flex items-center gap-2">
            <Briefcase size={16} className="text-blue-600" weight="fill" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Converted to Job</span>
            <Link to={`/jobs/${jr.converted_job_id}`} className="text-sm text-primary underline ml-1">View Job</Link>
          </div>
        </div>
      )}

      {/* Scope of Works — add retrospectively (any status); carried onto the job when converted and shown to the client */}
      <div className="mb-4">
        <ScopeOfWorksCard endpoint={`/job-requests/${id}`} value={jr.scope_of_works} caveats={jr.caveats} notice={jr.notice} siteNotes={jr.site_notes || ""} ai={jr.scope_ai} canDraftFromEmail={jr.source === "email"} onChanged={(p) => setJr((x) => ({ ...x, ...p }))} testid="jr-scope-card"
          context={{ entity: "request", category: jr.category, title: jr.title, description: jr.request_details, client_name: jr.client_company || jr.client_name, property_address: jr.property_address }}
          onSaved={(v, c, n) => setJr((p) => ({ ...p, scope_of_works: v, caveats: c, ...(n ? { notice: n } : {}) }))} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column: details */}
        <div className="lg:col-span-1 space-y-4">
          {/* Client details */}
          <div className="border border-border rounded-md p-4" data-testid="jr-client-info">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Client Details</h3>
            <div className="space-y-2 text-sm">
              {jr.client_name && <div className="flex items-center gap-2"><User size={14} className="text-muted-foreground shrink-0" /><span>{jr.client_name}</span></div>}
              {jr.client_company && <div className="flex items-center gap-2 text-muted-foreground"><span className="ml-5 text-xs">{jr.client_company}</span></div>}
              {jr.client_email && <div className="flex items-center gap-2"><EnvelopeSimple size={14} className="text-muted-foreground shrink-0" /><a href={`mailto:${jr.client_email}`} className="text-primary text-xs">{jr.client_email}</a></div>}
              {jr.client_phone && <div className="flex items-center gap-2"><Phone size={14} className="text-muted-foreground shrink-0" /><a href={`tel:${jr.client_phone}`} className="text-xs">{jr.client_phone}</a></div>}
              {(jr.client_phone || jr.requester_contact_phone) && (
                <div className="pt-1">
                  <WhatsAppSendButton phone={jr.client_phone || jr.requester_contact_phone} contactName={jr.client_name}
                                      contextType="job_request" contextId={jr.id} contextRef={jr.reference} />
                </div>
              )}
              {jr.requester_contact_name && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">Client Requester</span>
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium text-white"
                    style={{ backgroundColor: jr.requester_contact_color || "#3b82f6" }}
                    data-testid="jr-detail-requester-tag"
                  >
                    {jr.requester_contact_name}
                  </span>
                </div>
              )}
              {jr.property_address && <div className="flex items-start gap-2"><MapPin size={14} className="text-muted-foreground shrink-0 mt-0.5" /><span className="text-xs">{jr.property_address}</span></div>}
              {jr.what3words && <div className="pl-6"><What3WordsLink words={jr.what3words} /></div>}
            </div>
          </div>

          <PropertyAccessCard entityType="job_request" entityId={id} compact />

          {/* Job details */}
          <div className="border border-border rounded-md p-4" data-testid="jr-job-info">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Job Details</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Category</span><Badge variant="outline" className="text-[10px]">{jr.category?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</Badge></div>
              {jr.service_required && <div className="flex justify-between"><span className="text-muted-foreground">Service Required</span><Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 dark:text-blue-300" data-testid="jr-service-badge">{jr.service_required}</Badge></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">Priority</span><span className="font-semibold">{jr.priority?.toUpperCase()}</span></div>
              {jr.estimated_budget > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Estimated Budget</span><span className="font-semibold">£{jr.estimated_budget.toLocaleString()}</span></div>}
              {jr.requested_completion_date && <div className="flex justify-between"><span className="text-muted-foreground">Target Date</span><span>{fmtDate(jr.requested_completion_date)}</span></div>}
              {selectedQuote && <div className="flex justify-between border-t border-border pt-2 mt-2"><span className="text-muted-foreground">Selected Quote</span><span className="font-bold text-emerald-600">£{selectedQuote.amount.toLocaleString()}</span></div>}
            </div>
          </div>

          {/* Area matches — suppliers covering this property's location */}
          <AreaMatchSuppliers town={jr.town_city} county={jr.county} postcode={jr.postcode} service={jr.service_required} jrId={id} onQuoteRequested={fetchJR}
            askedEmails={(jr.timeline || []).filter(t => t.action === "quote_requested").flatMap(t => t.vendor_emails || [])} />

          {/* Request details (+ append-only client updates) */}
          {(jr.request_details || (jr.client_updates || []).length > 0) && (
            <div className={`border rounded-md p-4 ${jr.has_unseen_client_update ? "border-emerald-400 dark:border-emerald-700" : "border-border"}`} data-testid="jr-request-details">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Request Details</h3>
                {jr.has_unseen_client_update && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-400 text-emerald-700 dark:text-emerald-300" onClick={markUpdatesSeen} data-testid="jr-client-updates-seen-btn">
                    <Check size={11} className="mr-1" /> Mark client details as seen
                  </Button>
                )}
              </div>
              {jr.request_details && <p className="text-sm whitespace-pre-wrap">{jr.request_details}</p>}
              <ClientUpdatesList updates={jr.client_updates} viewer="admin" testPrefix="jr-client-update" />
            </div>
          )}

          {/* Attachments */}
          <div className="border border-border rounded-md p-4" data-testid="jr-attachments">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Attachments ({jr.attachments?.length || 0})</h3>
            {/* Image gallery thumbnails */}
            {allImages.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="jr-image-gallery">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => openLightbox(allImages, i)} className="h-14 w-14 rounded-md overflow-hidden border border-border hover:border-primary transition-colors" data-testid={`jr-gallery-img-${i}`}>
                    <AuthImage path={img.url} alt={img.name} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
            <FileDropZone
              files={jr.attachments || []}
              onFilesChange={handleAttachmentsChange}
              folder="job-requests"
              accept="all"
              maxFiles={20}
              compact
            />
          </div>
        </div>

        {/* Right column: tabs */}
        <div className="lg:col-span-2">
          <div className="mb-4 empty:hidden"><ResidentReportCard entityType="job_request" entityId={id} /></div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="quotes" data-testid="jr-tab-quotes">Quotes ({jr.quotes?.length || 0})</TabsTrigger>
              <TabsTrigger value="notes" data-testid="jr-tab-notes">Notes ({jr.notes?.length || 0}) <ClientWaitingPill item={waitingById[id]} className="ml-1.5" testId="jr-tab-notes-waiting" /></TabsTrigger>
              <TabsTrigger value="private" data-testid="jr-tab-private">Private</TabsTrigger>
              <TabsTrigger value="files" data-testid="jr-tab-files">Files</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="jr-tab-timeline">Timeline ({jr.timeline?.length || 0})</TabsTrigger>
            </TabsList>

            {/* Quotes Tab */}
            <TabsContent value="quotes" data-testid="jr-quotes-content">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Quotes</h3>
                <div className="flex items-center gap-2">
                  {(jr.quotes || []).length >= 2 && (
                    <Button size="sm" variant="outline" onClick={() => setShowCompare(true)} data-testid="jr-compare-quotes-btn">
                      <Crown size={14} className="mr-1 text-amber-500" /> Compare
                    </Button>
                  )}
                  {["draft", "awaiting_quotes", "quotes_received"].includes(jr.status) && (
                    <Button size="sm" variant="outline" onClick={openInviteDialog} data-testid="jr-email-vendors-btn"><PaperPlaneTilt size={14} className="mr-1" /> Invite Supplier</Button>
                  )}
                  {/* Phase 262/291 — Add Quote: Supplier (full line items) / In-House / Quick */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" data-testid="jr-add-quote-btn"><Plus size={14} className="mr-1" /> Add Quote <CaretDown size={11} className="ml-1" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/quotes/new?jr_id=${id}&source_type=supplier`)} data-testid="jr-add-quote-supplier">
                        <Truck size={14} className="mr-2 text-blue-600" />
                        <div>
                          <p className="text-xs font-medium">Add Supplier Quote</p>
                          <p className="text-[10px] text-muted-foreground">Full line-by-line quote with a linked supplier</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/quotes/new?jr_id=${id}`)} data-testid="jr-add-quote-in-house">
                        <House size={14} className="mr-2 text-emerald-600" />
                        <div>
                          <p className="text-xs font-medium">Create In-House Quote</p>
                          <p className="text-[10px] text-muted-foreground">Build a quote with line items</p>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowAddQuote(true)} data-testid="jr-add-quote-quick">
                        <Plus size={14} className="mr-2 text-slate-500" />
                        <div>
                          <p className="text-xs font-medium">Quick supplier quote</p>
                          <p className="text-[10px] text-muted-foreground">Just paste an amount (no line items)</p>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {(jr.quotes || []).length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-md">
                  <CurrencyGbp size={28} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-2">No quotes yet</p>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <Button size="sm" onClick={() => navigate(`/quotes/new?jr_id=${id}&source_type=supplier`)} data-testid="jr-empty-add-quote">
                      <Truck size={13} className="mr-1" /> Add Supplier Quote
                    </Button>
                    <Button size="sm" onClick={() => navigate(`/quotes/new?jr_id=${id}`)} data-testid="jr-empty-add-quote-in-house">
                      <House size={13} className="mr-1" /> Create In-House Quote
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowAddQuote(true)} data-testid="jr-empty-quick-quote">
                      <Plus size={13} className="mr-1" /> Quick add
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {jr.quotes.map(q => {
                    const isSelected = q.id === jr.selected_quote_id;
                    const cost = q.amount || 0;
                    const markupAPct = q.markup_a_pct || 0;
                    const markupBPct = q.markup_b_pct || 0;
                    const markupAAmount = cost * (markupAPct / 100);
                    const subtotal = cost + markupAAmount;
                    const markupBAmount = subtotal * (markupBPct / 100);
                    const total = subtotal + markupBAmount;
                    const hasMarkup = markupAPct > 0 || markupBPct > 0;
                    return (
                      <div key={q.id} className={`border rounded-md p-4 transition-colors ${isSelected ? "border-emerald-400 dark:border-emerald-600 bg-emerald-50/50 dark:bg-emerald-900/10" : "border-border"}`} data-testid={`jr-quote-${q.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {q.source_type === "in_house" ? (
                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[9px] gap-1" data-testid={`quote-badge-in-house-${q.id}`}>
                                  <House size={10} weight="fill" /> In-House
                                </Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[9px] gap-1" data-testid={`quote-badge-supplier-${q.id}`}>
                                  <Truck size={10} weight="fill" /> Supplier
                                </Badge>
                              )}
                              <p className="text-sm font-semibold">{q.vendor_name}</p>
                              {isSelected && <Badge className="bg-emerald-600 text-white text-[9px]">Selected</Badge>}
                              {q.recommended && !isSelected && <Badge variant="secondary" className="text-[9px]">Recommended</Badge>}
                              <ScopeDriftPill flag={builderQuotes[q.in_house_quote_id]?.scope_drift} testId={`jr-quote-${q.id}-scope-drift`} />
                            </div>
                            {q.vendor_company && q.source_type !== "in_house" && <p className="text-xs text-muted-foreground">{q.vendor_company}</p>}
                            {q.source_type === "in_house" && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {q.quote_reference} · {q.line_item_count || 0} line item{q.line_item_count === 1 ? "" : "s"}
                              </p>
                            )}
                            {q.in_house_quote_id && (
                              <div className="mt-1.5">
                                <QuoteQuickActions quoteId={q.in_house_quote_id} reference={q.quote_reference} onChanged={fetchJR} testId={`jr-quote-${q.id}`} />
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold font-[Chivo]" data-testid={`quote-cost-${q.id}`}>£{cost.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
                            {hasMarkup ? (
                              <p className="text-[10px] text-muted-foreground">cost price</p>
                            ) : q.source_type === "in_house" ? (
                              <p className="text-[10px] text-muted-foreground">ex VAT</p>
                            ) : null}
                          </div>
                        </div>

                        {/* Markup Breakdown */}
                        {hasMarkup && (
                          <div className="mb-3 border border-border rounded-md overflow-hidden bg-muted/20" data-testid={`quote-markup-${q.id}`}>
                            <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Markup Breakdown</p>
                            </div>
                            <div className="px-3 py-2 space-y-1.5 text-xs">
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Cost</span>
                                <span className="font-mono font-medium">£{cost.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                              </div>
                              {markupAPct > 0 && (
                                <>
                                  <div className="flex justify-between items-center text-primary">
                                    <span>+ {q.markup_a_label || "Markup A"} ({markupAPct}%)</span>
                                    <span className="font-mono font-medium">£{markupAAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                                  </div>
                                  <div className="flex justify-between items-center border-t border-border/50 pt-1">
                                    <span className="font-semibold">Subtotal</span>
                                    <span className="font-mono font-bold">£{subtotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                                  </div>
                                </>
                              )}
                              {markupBPct > 0 && (
                                <>
                                  <div className="flex justify-between items-center text-violet-600 dark:text-violet-400">
                                    <span>+ {q.markup_b_label || "Markup B"} ({markupBPct}%)</span>
                                    <span className="font-mono font-medium">£{markupBAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                                  </div>
                                </>
                              )}
                              <div className="flex justify-between items-center border-t border-border pt-1.5">
                                <span className="font-bold text-sm">Total (to Client)</span>
                                <span className="font-mono font-bold text-sm" data-testid={`quote-total-${q.id}`}>£{total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                              </div>
                            </div>
                            {/* Profit Box */}
                            {markupAPct > 0 && (
                              <div className="px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border-t border-emerald-200 dark:border-emerald-800" data-testid={`quote-profit-${q.id}`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Our Profit</span>
                                  <span className="text-sm font-bold font-mono text-emerald-700 dark:text-emerald-400">£{markupAAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                                </div>
                                <p className="text-[10px] text-emerald-600 dark:text-emerald-500 mt-0.5">Cost £{cost.toLocaleString("en-GB", { minimumFractionDigits: 2 })} → Markup {markupAPct}% = £{markupAAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })} profit</p>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-2">
                          {q.vendor_email && <span className="flex items-center gap-1"><EnvelopeSimple size={12} /> {q.vendor_email}</span>}
                          {q.vendor_phone && <span className="flex items-center gap-1"><Phone size={12} /> {q.vendor_phone}</span>}
                          {q.validity_date && <span className="flex items-center gap-1"><Clock size={12} /> Valid until: {q.validity_date}</span>}
                        </div>
                        {q.description && <p className="text-xs mb-2">{q.description}</p>}
                        {q.line_items?.length > 0 && (
                          <div className="mb-2 border border-border rounded overflow-hidden" data-testid={`jr-quote-lines-${q.id}`}>
                            <table className="w-full text-[11px]">
                              <thead className="bg-muted/50 text-muted-foreground">
                                <tr>
                                  <th className="text-left px-2 py-1 font-medium">Item</th>
                                  <th className="text-right px-2 py-1 font-medium w-12">Qty</th>
                                  <th className="text-right px-2 py-1 font-medium w-20">Unit £</th>
                                  <th className="text-right px-2 py-1 font-medium w-20">Total £</th>
                                </tr>
                              </thead>
                              <tbody>
                                {q.line_items.map((li, i) => (
                                  <tr key={li.id || i} className="border-t border-border">
                                    <td className="px-2 py-1">{li.description}</td>
                                    <td className="px-2 py-1 text-right">{li.quantity}</td>
                                    <td className="px-2 py-1 text-right">{Number(li.unit_cost || li.unit_price || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                                    <td className="px-2 py-1 text-right font-medium">{Number(li.total || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                                  </tr>
                                ))}
                                {q.vat_enabled && (
                                  <>
                                    <tr className="border-t border-border text-muted-foreground">
                                      <td className="px-2 py-1" colSpan={3}>Subtotal</td>
                                      <td className="px-2 py-1 text-right">{Number(q.subtotal || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                    <tr className="text-muted-foreground">
                                      <td className="px-2 py-1" colSpan={3}>VAT ({q.vat_rate}%)</td>
                                      <td className="px-2 py-1 text-right">{Number(q.vat_amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {q.attachments?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {q.attachments.map((a, i) => (
                              <button key={i} type="button" onClick={() => openAuthFile(a.url)} className="flex items-center gap-1 text-[10px] bg-muted px-2 py-1 rounded hover:bg-muted/80">
                                <FileIcon size={10} /> {a.name}
                              </button>
                            ))}
                          </div>
                        )}
                        {(q.proposed_start_date || q.admin_counter_start_date) && (
                          <div className="mt-2 p-2 rounded-md bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 text-[11px] space-y-1" data-testid={`jr-start-date-strip-${q.id}`}>
                            {q.proposed_start_date && (
                              <div><span className="text-purple-900 dark:text-purple-100 font-semibold">Supplier proposed start:</span> <strong>{q.proposed_start_date}</strong>{q.start_date_note ? ` — "${q.start_date_note}"` : ""}{q.counter_accepted_at ? " ✓ counter accepted" : ""}</div>
                            )}
                            {q.admin_counter_start_date && !q.counter_accepted_at && (
                              <div><span className="text-purple-900 dark:text-purple-100 font-semibold">You countered with:</span> <strong>{q.admin_counter_start_date}</strong>{q.admin_counter_note ? ` — "${q.admin_counter_note}"` : ""} <em className="text-purple-700 dark:text-purple-300">— awaiting supplier</em></div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-between pt-2 border-t border-border">
                          <div className="flex items-center gap-2">
                            <p className="text-[10px] text-muted-foreground">Added by {q.added_by_name} on {new Date(q.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                            {q.submitted_via === "portal" && <span className="text-[10px] bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 px-1.5 py-0.5 rounded-full" data-testid={`quote-portal-badge-${q.id}`}>via portal</span>}
                          </div>
                          <div className="flex gap-1">
                            {!isSelected && <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => handleSelectQuote(q.id)} data-testid={`jr-select-quote-${q.id}`}><Star size={12} className="mr-0.5" /> Select</Button>}
                            {q.proposed_start_date && !q.counter_accepted_at && (
                              <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => setCounteringQuote(q)} data-testid={`jr-counter-quote-${q.id}`}><CalendarPlus size={12} className="mr-0.5" /> Counter date</Button>
                            )}
                            <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => setReverseCalcQuote(q)} data-testid={`jr-reverse-calc-${q.id}`}><ArrowsClockwise size={12} className="mr-0.5" /> Reverse calc</Button>
                            <Button variant="outline" size="sm" className="text-[10px] h-6" onClick={() => { setEditingQuote(q.id); setEditQuoteForm({ vendor_name: q.vendor_name, vendor_email: q.vendor_email, vendor_phone: q.vendor_phone, vendor_company: q.vendor_company, amount: q.amount, description: q.description, validity_date: q.validity_date, attachments: q.attachments || [], markup_a_pct: q.markup_a_pct || 0, markup_b_pct: q.markup_b_pct || 0, markup_a_label: q.markup_a_label || "Our Mark Up", markup_b_label: q.markup_b_label || "Client Mark Up" }); }} data-testid={`jr-edit-quote-${q.id}`}><PencilSimple size={12} className="mr-0.5" /> Edit</Button>
                            <Button variant="ghost" size="sm" className="text-[10px] h-6 text-destructive" onClick={() => handleDeleteQuote(q.id)} data-testid={`jr-delete-quote-${q.id}`}><Trash size={12} /></Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {jr.quotes.length >= 2 && (
                    <div className="border border-border rounded-md p-3 bg-muted/30" data-testid="jr-quote-comparison">
                      <h4 className="text-xs font-semibold mb-2">Quote Comparison</h4>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Lowest:</span> <span className="font-bold">£{Math.min(...jr.quotes.map(q => q.amount)).toLocaleString()}</span></div>
                        <div><span className="text-muted-foreground">Highest:</span> <span className="font-bold">£{Math.max(...jr.quotes.map(q => q.amount)).toLocaleString()}</span></div>
                        <div><span className="text-muted-foreground">Average:</span> <span className="font-bold">£{Math.round(jr.quotes.reduce((s, q) => s + q.amount, 0) / jr.quotes.length).toLocaleString()}</span></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" data-testid="jr-notes-content">
              <NotesThread entityType="job-request" entityId={id} onChanged={fetchJR} folder="job-request-notes" />
            </TabsContent>

            {/* Private admin notes thread */}
            <TabsContent value="private" data-testid="jr-private-content">
              <PrivateNotes entityType="job_request" entityId={id} />
            </TabsContent>

            {/* Files Tab — central File Hub folder for this request */}
            <TabsContent value="files" data-testid="jr-files-content">
              <EntityFiles entityType="job_request" entityId={id} currentUser={user} />
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline" data-testid="jr-timeline-content">
              <div className="relative pl-6">
                <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                {[...(jr.timeline || [])].reverse().map(t => (
                  <div key={t.id} className="relative mb-4" data-testid={`jr-timeline-${t.id}`}>
                    <div className={`absolute left-[-18px] top-1 h-3 w-3 rounded-full border-2 border-background ${t.action === "created" ? "bg-primary" : t.action === "converted_to_project" ? "bg-emerald-500" : t.action === "status_change" ? "bg-blue-500" : t.action === "quote_added" || t.action === "quote_selected" ? "bg-amber-500" : t.action === "quote_requested" ? "bg-violet-500" : t.action === "client_response" ? "bg-teal-500" : "bg-slate-400"}`} />
                    <div>
                      <p className="text-sm">{t.description}</p>
                      {t.vendor_emails?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`timeline-emails-${t.id}`}>
                          {t.vendor_emails.map((em, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-800 px-2 py-0.5 rounded-full font-mono">
                              {em}
                            </span>
                          ))}
                        </div>
                      )}
                      {t.action === "quote_added" && t.description?.includes("submitted online") && (
                        <span className="inline-flex items-center text-[10px] bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 px-2 py-0.5 rounded-full mt-1">via supplier portal</span>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Phase 292 — Side-by-side quote comparison */}
      <QuoteComparisonDialog
        jrId={id}
        open={showCompare}
        onClose={() => setShowCompare(false)}
        onSelected={fetchJR}
        onSelectQuote={(qid) => handleSelectQuote(qid)}
      />

      {/* Add Quote Dialog */}
      <Dialog open={showAddQuote} onOpenChange={setShowAddQuote}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Add Quote</DialogTitle>
            <DialogDescription>Enter supplier quote details. All entries are timestamped.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddQuote} className="space-y-3">
            {/* Onboarded supplier picker — picking auto-fills vendor_*
                fields and ties the quote back to the supplier record via
                vendor_id. Leave empty for manual entry (the existing fields
                below stay fully editable so admin can tweak if needed). */}
            <SupplierPicker
              suppliers={onboardedSuppliers}
              selectedId={quoteForm.vendor_id}
              onPick={(supplier) => {
                if (!supplier) {
                  setQuoteForm(prev => ({ ...prev, vendor_id: "" }));
                  return;
                }
                setQuoteForm(prev => ({
                  ...prev,
                  vendor_id: supplier.id || "",
                  vendor_name: supplier.contact_name || supplier.name || prev.vendor_name,
                  vendor_email: supplier.email || prev.vendor_email,
                  vendor_phone: supplier.phone || prev.vendor_phone,
                  vendor_company: supplier.company_name || supplier.name || prev.vendor_company,
                }));
              }}
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Supplier / Contractor Name *</Label><Input value={quoteForm.vendor_name} onChange={e => setQuoteForm({ ...quoteForm, vendor_name: e.target.value })} required data-testid="quote-vendor-name" /></div>
              <div><Label>Email</Label><Input type="email" value={quoteForm.vendor_email} onChange={e => setQuoteForm({ ...quoteForm, vendor_email: e.target.value })} data-testid="quote-vendor-email" /></div>
              <div><Label>Phone</Label><Input value={quoteForm.vendor_phone} onChange={e => setQuoteForm({ ...quoteForm, vendor_phone: e.target.value })} /></div>
              <div><Label>Company</Label><Input value={quoteForm.vendor_company} onChange={e => setQuoteForm({ ...quoteForm, vendor_company: e.target.value })} /></div>
              <div><Label>Quote Amount (£) *</Label><Input type="number" step="0.01" value={quoteForm.amount} onChange={e => setQuoteForm({ ...quoteForm, amount: e.target.value })} required data-testid="quote-amount" /></div>
            </div>
            <div><Label>Valid Until</Label><DateInput value={quoteForm.validity_date} onChange={e => setQuoteForm({ ...quoteForm, validity_date: e.target.value })} /></div>
            <div><Label>Description / Scope</Label><Textarea value={quoteForm.description} onChange={e => setQuoteForm({ ...quoteForm, description: e.target.value })} rows={2} placeholder="What does this quote cover?" /></div>

            {/* Markup Section */}
            <MarkupFields form={quoteForm} setForm={setQuoteForm} templates={markupTemplates} />

            <div>
              <Label>Attachments</Label>
              <FileDropZone
                files={quoteForm.attachments}
                onFilesChange={(files) => setQuoteForm(prev => ({ ...prev, attachments: files }))}
                folder="job-request-quotes"
                accept="all"
                maxFiles={5}
                compact
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddQuote(false)}>Cancel</Button>
              <Button type="submit" disabled={addingQuote} data-testid="quote-submit-btn">{addingQuote ? "Adding..." : "Add Quote"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert to Project Dialog */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent className="max-w-md" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Convert to Project</DialogTitle>
            <DialogDescription>This will create a new project from this job request with all the associated data.{jr.client_id ? " The client will be notified and can follow it in their portal's Projects section." : ""}</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2 text-sm">
            <p><strong>Title:</strong> {jr.title}</p>
            <p><strong>Client:</strong> {jr.client_name}</p>
            <p><strong>Property:</strong> {jr.property_address}</p>
            {selectedQuote ? (
              <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-md">
                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Selected Quote</p>
                <p className="font-bold">£{selectedQuote.amount.toLocaleString()} — {selectedQuote.vendor_name}</p>
              </div>
            ) : (
              <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-md p-2" data-testid="convert-no-quote-note">No quote selected — the project budget will use the request's indicative budget{jr.estimated_budget > 0 ? ` (£${Number(jr.estimated_budget).toLocaleString()})` : ""}. You can add tasks and costs on the project afterwards.</p>
            )}
            <p className="text-xs text-muted-foreground">All attachments, notes, and quote details will be carried across to the new project.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvert(false)}>Cancel</Button>
            <Button onClick={handleConvert} disabled={converting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-convert-btn">{converting ? "Converting..." : "Convert to Project"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm" data-testid="delete-jr-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive font-[Chivo]">Delete Job Request</DialogTitle>
            <DialogDescription>This will permanently delete this job request and all associated quotes and notes.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-jr-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Details Dialog */}
      <Dialog open={showEditDetails} onOpenChange={setShowEditDetails}>
        <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="edit-jr-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Edit Job Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={editForm.title || ""} onChange={e => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Client Name</Label><Input value={editForm.client_name || ""} onChange={e => setEditForm({ ...editForm, client_name: e.target.value })} /></div>
              <div><Label>Client Company</Label><Input value={editForm.client_company || ""} onChange={e => setEditForm({ ...editForm, client_company: e.target.value })} /></div>
              <div><Label>Client Email</Label><Input value={editForm.client_email || ""} onChange={e => setEditForm({ ...editForm, client_email: e.target.value })} /></div>
              <div><Label>Client Phone</Label><Input value={editForm.client_phone || ""} onChange={e => setEditForm({ ...editForm, client_phone: e.target.value })} /></div>
            </div>
            <div>
              <Label>Property Address</Label>
              <AddressAutocomplete
                value={editForm.property_address || ""}
                onChange={v => setEditForm({ ...editForm, property_address: v })}
                onSelect={(a) => setEditForm(f => ({
                  ...f,
                  property_address: a.formatted_address || f.property_address,
                  street_address: a.street_address || "",
                  town_city: a.town_city || "",
                  county: a.county || "",
                  postcode: a.postcode || "",
                  latitude: a.latitude || null,
                  longitude: a.longitude || null,
                }))}
                data-testid="jr-edit-address"
              />
            </div>
            <div>
              <Label>what3words <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
              <Input value={editForm.what3words || ""} onChange={e => setEditForm({ ...editForm, what3words: e.target.value })} placeholder="///filled.count.soap" className="mt-1 font-mono" data-testid="jr-edit-what3words" />
            </div>
            <AccessNotesField value={editForm.access_notes || ""} onChange={(v) => setEditForm(f => ({ ...f, access_notes: v }))} propertyLinked={!!jr.property_id} testid="jr-edit-access-notes" />
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Category</Label>
                <Select value={editForm.category || "general"} onValueChange={v => setEditForm({ ...editForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["general", "maintenance", "refurbishment", "new_build", "renovation", "fit_out", "planned_works", "reactive_repair", "inspection", "electrical", "plumbing", "roofing", "structural", "other"].map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase())}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Service Required</Label>
                <Select value={editForm.service_required || "__none__"} onValueChange={v => setEditForm({ ...editForm, service_required: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="jr-edit-service-required"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not specified</SelectItem>
                    {serviceOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={editForm.priority || "medium"} onValueChange={v => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "medium", "high", "urgent"].map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Target Date</Label><DateInput value={editForm.requested_completion_date || ""} onChange={e => setEditForm({ ...editForm, requested_completion_date: e.target.value })} /></div>
              <div><Label>Estimated Budget (£)</Label><Input type="number" step="0.01" value={editForm.estimated_budget || ""} onChange={e => setEditForm({ ...editForm, estimated_budget: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div><Label>Request Details</Label><Textarea value={editForm.request_details || ""} onChange={e => setEditForm({ ...editForm, request_details: e.target.value })} rows={4} /></div>
            <ScopeOfWorksField value={editForm.scope_of_works} onChange={(v) => setEditForm(f => ({ ...f, scope_of_works: v }))} testid="jr-edit-scope-of-works"
              sets={{ caveats: editForm.caveats, notice: editForm.notice, onApply: (v) => setEditForm(f => ({ ...f, ...v })) }}
              context={{ entity: "request", title: editForm.title, description: editForm.request_details, client_name: editForm.client_company || editForm.client_name, property_address: editForm.property_address }} />
            <CaveatsField value={editForm.caveats} onChange={(v) => setEditForm(f => ({ ...f, caveats: v }))} scope={editForm.scope_of_works} testid="jr-edit-caveats"
              context={{ entity: "request", category: editForm.category, title: editForm.title, description: editForm.request_details, client_name: editForm.client_company || editForm.client_name, property_address: editForm.property_address }} />
            <NoticeEditor value={editForm.notice} onChange={(n) => setEditForm({ ...editForm, notice: n })} clientToggle aiContext={{ entity: "job_request", title: editForm.title, description: editForm.description }} testid="jr-edit-notice" compact />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDetails(false)}>Cancel</Button>
            <Button onClick={handleSaveDetails} data-testid="save-jr-details-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quote Dialog */}
      <Dialog open={!!editingQuote} onOpenChange={open => { if (!open) setEditingQuote(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Edit Quote</DialogTitle>
            <DialogDescription>Update supplier quote details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditQuote} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Supplier / Contractor Name *</Label><Input value={editQuoteForm.vendor_name || ""} onChange={e => setEditQuoteForm({ ...editQuoteForm, vendor_name: e.target.value })} required data-testid="edit-quote-vendor-name" /></div>
              <div><Label>Email</Label><Input type="email" value={editQuoteForm.vendor_email || ""} onChange={e => setEditQuoteForm({ ...editQuoteForm, vendor_email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={editQuoteForm.vendor_phone || ""} onChange={e => setEditQuoteForm({ ...editQuoteForm, vendor_phone: e.target.value })} /></div>
              <div><Label>Company</Label><Input value={editQuoteForm.vendor_company || ""} onChange={e => setEditQuoteForm({ ...editQuoteForm, vendor_company: e.target.value })} /></div>
              <div><Label>Quote Amount (£) *</Label><Input type="number" step="0.01" value={editQuoteForm.amount || ""} onChange={e => setEditQuoteForm({ ...editQuoteForm, amount: e.target.value })} required data-testid="edit-quote-amount" /></div>
            </div>
            <div><Label>Valid Until</Label><DateInput value={editQuoteForm.validity_date || ""} onChange={e => setEditQuoteForm({ ...editQuoteForm, validity_date: e.target.value })} /></div>
            <div><Label>Description / Scope</Label><Textarea value={editQuoteForm.description || ""} onChange={e => setEditQuoteForm({ ...editQuoteForm, description: e.target.value })} rows={2} /></div>

            {/* Markup Section */}
            <MarkupFields form={editQuoteForm} setForm={setEditQuoteForm} templates={markupTemplates} />

            <div>
              <Label>Attachments</Label>
              <FileDropZone
                files={editQuoteForm.attachments || []}
                onFilesChange={(files) => setEditQuoteForm(prev => ({ ...prev, attachments: files }))}
                folder="job-request-quotes"
                accept="all"
                maxFiles={5}
                compact
                className="mt-1"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingQuote(null)}>Cancel</Button>
              <Button type="submit" data-testid="save-quote-btn">Save Quote</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Supplier to Quote Dialog */}
      <Dialog open={showRequestQuotes} onOpenChange={v => { setShowRequestQuotes(v); if (!v) { setSupplierSearch(""); setSelectedSuppliers([]); setVendorEmailsInput(""); } }}>
        <DialogContent className="max-w-lg" data-testid="request-quotes-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Invite Supplier to Quote</DialogTitle>
            <DialogDescription>Select suppliers from your directory or enter email addresses manually. Each supplier will receive a professional email with a <strong>Job Request Brief PDF</strong> attached.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Supplier Picker */}
            <div>
              <Label className="text-xs font-semibold">Choose from Suppliers</Label>
              <Input
                placeholder="Search by company, email, or trade..."
                value={supplierSearch}
                onChange={e => setSupplierSearch(e.target.value)}
                className="mt-1.5 text-xs"
                data-testid="supplier-search-input"
              />
              <div className="mt-2 max-h-44 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {filteredSuppliers.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No suppliers found</p>
                ) : (
                  filteredSuppliers.slice(0, 30).map(s => {
                    const isSelected = selectedSuppliers.some(sel => sel.id === s.id);
                    return (
                      <button key={s.id} type="button"
                        onClick={() => toggleSupplier(s)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected ? "bg-primary/5" : "hover:bg-muted/50"}`}
                        data-testid={`supplier-pick-${s.id}`}
                      >
                        <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"}`}>
                          {isSelected && <CheckCircle size={12} className="text-primary-foreground" weight="bold" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium truncate">{s.company_name}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{s.email} {s.trade ? `\u2022 ${s.trade}` : ""}</p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
              {selectedSuppliers.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {selectedSuppliers.map(s => (
                    <Badge key={s.id} variant="secondary" className="text-[10px] gap-1 pr-1">
                      {s.company_name}
                      <button type="button" onClick={() => toggleSupplier(s)} className="ml-0.5 hover:text-destructive">
                        <X size={10} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Manual Email Input */}
            <div>
              <Label className="text-xs font-semibold">Or Enter Email Addresses</Label>
              <Textarea
                value={vendorEmailsInput}
                onChange={e => setVendorEmailsInput(e.target.value)}
                rows={3}
                placeholder={"supplier1@email.com\nsupplier2@email.com"}
                className="mt-1.5 font-mono text-xs"
                data-testid="vendor-emails-input"
              />
              <p className="text-[10px] text-muted-foreground mt-1">One email per line, or separate with commas. For suppliers not yet in your directory.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestQuotes(false)}>Cancel</Button>
            <Button onClick={handleRequestVendorQuotes} disabled={sendingQuoteRequests || (!selectedSuppliers.length && !vendorEmailsInput.trim())} data-testid="send-quote-requests-btn">
              <PaperPlaneTilt size={14} className="mr-1" /> {sendingQuoteRequests ? "Sending..." : `Send Invite${selectedSuppliers.length + (vendorEmailsInput.trim() ? 1 : 0) > 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Approval Link Dialog */}
      <Dialog open={showApprovalLink} onOpenChange={setShowApprovalLink}>
        <DialogContent className="max-w-md" data-testid="approval-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Client Approval Link</DialogTitle>
            <DialogDescription>Share this link with your client. They can review the quote and approve or decline without logging in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input value={approvalLink} readOnly className="font-mono text-xs" data-testid="approval-link-value" />
              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(approvalLink); toast.success("Link copied!"); }} data-testid="copy-approval-link-btn">
                <CopySimple size={14} />
              </Button>
            </div>
            {jr.client_email && (
              <Button variant="outline" className="w-full text-xs" onClick={() => handleGenerateApprovalLink(true)} disabled={generatingLink} data-testid="email-approval-link-btn">
                <EnvelopeSimple size={14} className="mr-1" /> Email Link to {jr.client_email}
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowApprovalLink(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Job Dialog */}
      <RamsAfterConvertDialog open={ramsAfter.open} jobId={ramsAfter.jobId} jobRef={ramsAfter.jobRef} requestId={id} autoPending={ramsAfter.autoPending} onClose={() => setRamsAfter(s => ({ ...s, open: false }))} />
      <Dialog open={showConvertJob} onOpenChange={setShowConvertJob}>
        <DialogContent className={`${splitEnabled ? "sm:max-w-2xl" : "sm:max-w-md"} max-h-[90vh] overflow-y-auto`} data-testid="convert-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Convert to Job</DialogTitle>
            <DialogDescription>
              This will create a new Job with all client and property details from this request.
              You can then schedule the job, assign a vendor, and attach job forms.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm border border-border rounded-md p-3 bg-muted/30">
            <p><span className="text-muted-foreground">Title:</span> {jr.title}</p>
            <p><span className="text-muted-foreground">Client:</span> {jr.client_name}</p>
            <p><span className="text-muted-foreground">Property:</span> {jr.property_address}</p>
            {selectedQuote && <p><span className="text-muted-foreground">Selected Supplier:</span> {selectedQuote.vendor_name}</p>}
          </div>

          {/* Split into sub-jobs option */}
          <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition ${splitEnabled ? "border-violet-500 bg-violet-50/50 dark:bg-violet-950/20" : "border-border bg-muted/20"}`} data-testid="convert-split-toggle">
            <input type="checkbox" checked={splitEnabled} onChange={(e) => setSplitEnabled(e.target.checked)} className="mt-0.5 h-4 w-4" data-testid="convert-split-checkbox" />
            <div className="flex-1 text-sm">
              <p className="font-medium flex items-center gap-1.5"><TreeStructure size={14} weight="duotone" className="text-violet-600" /> Split into sub-jobs — one per issue</p>
              <p className="text-xs text-muted-foreground mt-0.5">The property becomes the main job; each ticked issue becomes its own sub-job you can assign to a different trade. The main job completes itself when every sub-job is done. Sub-jobs start unassigned.</p>
            </div>
          </label>
          {splitEnabled && <SplitIssuesEditor rows={splitRows} onChange={setSplitRows} requestId={id} requestText={jr.request_details} />}

          {/* Works Order option */}
          {!splitEnabled && (
          <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition ${convertCreateWO ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" : "border-border bg-muted/20"}`} data-testid="convert-create-wo-toggle">
            <input
              type="checkbox"
              checked={convertCreateWO}
              onChange={(e) => setConvertCreateWO(e.target.checked)}
              className="mt-0.5 h-4 w-4"
              data-testid="convert-create-wo-checkbox"
            />
            <div className="flex-1 text-sm">
              <p className="font-medium flex items-center gap-1.5"><Receipt size={14} weight="duotone" className="text-blue-600" /> Also create a draft Works Order</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedQuote
                  ? <>Pulls the supplier (<span className="font-medium">{selectedQuote.vendor_name || "—"}</span>) and quote line items into a draft WO you can edit before sending.</>
                  : <>Creates an empty draft Works Order linked to this job — you can add line items and assign a supplier afterwards.</>}
              </p>
            </div>
          </label>
          )}

          {/* Notify losing suppliers option — only shown when there are actual
              supplier quotes to notify (other than the selected winner). */}
          {(() => {
            const losingSupplierEmails = (jr.quotes || [])
              .filter(q => q.id !== jr.selected_quote_id)
              .filter(q => (q.source_type || "supplier") === "supplier")
              .map(q => (q.vendor_email || "").trim().toLowerCase())
              .filter(e => e && e.includes("@"));
            const uniqueCount = new Set(losingSupplierEmails).size;
            if (uniqueCount === 0) return null;
            return (
              <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition ${notifyLosingSuppliers ? "border-amber-500 bg-amber-50/50 dark:bg-amber-950/20" : "border-border bg-muted/20"}`} data-testid="notify-losing-suppliers-toggle">
                <input
                  type="checkbox"
                  checked={notifyLosingSuppliers}
                  onChange={(e) => setNotifyLosingSuppliers(e.target.checked)}
                  className="mt-0.5 h-4 w-4"
                  data-testid="notify-losing-suppliers-checkbox"
                />
                <div className="flex-1 text-sm">
                  <p className="font-medium flex items-center gap-1.5"><EnvelopeSimple size={14} weight="duotone" className="text-amber-600" /> Notify losing suppliers</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Sends a warm, appreciative email to the <span className="font-medium">{uniqueCount}</span> other supplier{uniqueCount === 1 ? "" : "s"} who quoted, letting them know you went with someone else and keeping the door open for future work. No prices or winner details are shared.
                  </p>
                </div>
              </label>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertJob(false)}>Cancel</Button>
            <Button onClick={handleConvertToJob} disabled={convertingJob} data-testid="confirm-convert-job-btn" className="bg-blue-600 hover:bg-blue-700">
              <Briefcase size={14} className="mr-1" /> {convertingJob ? "Converting..." : splitEnabled ? `Create main job + ${selectedIssues(splitRows).length} sub-job${selectedIssues(splitRows).length === 1 ? "" : "s"}` : "Convert to Job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Counter-propose start date dialog */}
      <Dialog open={!!counteringQuote} onOpenChange={(o) => { if (!o) { setCounteringQuote(null); setCounterForm({ counter_start_date: "", note: "" }); } }}>
        <DialogContent className="max-w-md" data-testid="jr-counter-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Counter-propose start date</DialogTitle>
            <DialogDescription>
              {counteringQuote?.vendor_name ? `Reply to ${counteringQuote.vendor_name}'s ` : "Reply to the supplier's "}
              proposed start date{counteringQuote?.proposed_start_date ? ` of ${counteringQuote.proposed_start_date}` : ""} with a different date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="counter-date">Counter start date <span className="text-red-600">*</span></Label>
              <DateInput
                id="counter-date"
                value={counterForm.counter_start_date}
                onChange={(e) => setCounterForm((f) => ({ ...f, counter_start_date: e.target.value }))}
                data-testid="jr-counter-date-input"
              />
            </div>
            <div>
              <Label htmlFor="counter-note">Note (optional)</Label>
              <Textarea
                id="counter-note"
                rows={3}
                placeholder="e.g. Our team is fully booked that week — could you start Monday after?"
                value={counterForm.note}
                onChange={(e) => setCounterForm((f) => ({ ...f, note: e.target.value }))}
                data-testid="jr-counter-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCounteringQuote(null); setCounterForm({ counter_start_date: "", note: "" }); }} data-testid="jr-counter-cancel-btn">Cancel</Button>
            <Button onClick={handleCounterSubmit} data-testid="jr-counter-submit-btn">Send counter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reverse pricing calculator (for any selected supplier quote row) */}
      <ReverseCalcDialog
        open={!!reverseCalcQuote}
        onOpenChange={(o) => { if (!o) setReverseCalcQuote(null); }}
        initialMarkupA={reverseCalcQuote?.markup_a_pct || 0}
        initialMarkupB={reverseCalcQuote?.markup_b_pct || 0}
        initialMarkupALabel={reverseCalcQuote?.markup_a_label || "Our Mark Up"}
        initialMarkupBLabel={reverseCalcQuote?.markup_b_label || "Client Mark Up"}
        initialVatEnabled={false}
        initialVatRate={20}
        initialTotal={(() => {
          if (!reverseCalcQuote) return 1000;
          const cost = reverseCalcQuote.amount || 0;
          const a = cost * (1 + (reverseCalcQuote.markup_a_pct || 0) / 100);
          const b = a * (1 + (reverseCalcQuote.markup_b_pct || 0) / 100);
          return b > 0 ? b : 1000;
        })()}
        contextLabel={reverseCalcQuote ? `${reverseCalcQuote.vendor_name || ""} · cost £${(reverseCalcQuote.amount || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : ""}
      />
    </div>
  );
}
